/* ──────────────────────────────────────────────────────────────
   PostHog wiring for Mowies Bali.

   - Loads the PostHog JS snippet dynamically if window.__PH_KEY
     is set (the layout exposes it from config).
   - Enables autocapture ($pageview, $autocapture, $pageleave) so
     every link/button/form is recorded with zero per-element work.
   - Adds PostHog tracing headers to fetch() requests going to our
     own backend so server-side captures + errors stitch to the
     same session and distinct_id as the frontend.
   - Delegates clicks on [data-ph-event] so custom semantic events
     live in markup, not inline onclick handlers.

   Nothing runs if the project token isn't configured.
   ────────────────────────────────────────────────────────────── */

const PH_HOST = window.__PH_HOST || 'https://us.i.posthog.com';
const PH_KEY = window.__PH_KEY || null;
const PH_BACKEND_HOST = window.__PH_BACKEND_HOST || window.location.host;

if (PH_KEY) {
    // 1. Install the PostHog stub — sets up window.posthog with queueable
    //    method shims (capture, identify, etc.) and declares posthog.init.
    installPosthogStub();

    // 2. Call posthog.init() immediately. The stub's init does two things:
    //    it appends a <script src=".../static/array.js"> tag (which, once
    //    loaded, replaces the stubs with the real SDK and replays queued
    //    calls), and it pushes the [key, opts] into posthog._i so the real
    //    SDK knows how to configure itself.
    window.posthog.init(PH_KEY, {
        api_host: PH_HOST,
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        session_recording: { maskAllInputs: true },
        // Send X-POSTHOG-DISTINCT-ID and X-POSTHOG-SESSION-ID to our own
        // backend so server-side captures + exceptions stitch to the same
        // session_id and distinct_id as frontend events.
        __add_tracing_headers: [PH_BACKEND_HOST],
    });

    // 3. Wire up click delegation synchronously. posthog.capture() calls
    //    made before the real SDK loads are queued by the stub and replayed
    //    once /static/array.js finishes — so there's no reason to wait.
    wireDelegatedClickTracking();
    wireLivewireHooks();
}

/* The official PostHog web loader, minified.
   Safe to call multiple times — it no-ops on subsequent calls because
   the check of `e.__SV` short-circuits the IIFE body. */
function installPosthogStub() {
    (function (t, e) {
        var o, n, p, r;
        e.__SV ||
            ((window.posthog = e),
            (e._i = []),
            (e.init = function (i, s, a) {
                function g(t, e) {
                    var o = e.split('.');
                    2 == o.length && ((t = t[o[0]]), (e = o[1])),
                        (t[e] = function () {
                            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                        });
                }
                ((p = t.createElement('script')).type = 'text/javascript'),
                    (p.crossOrigin = 'anonymous'),
                    (p.async = !0),
                    (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
                    (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
                var u = e;
                for (
                    void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
                        u.people = u.people || [],
                        u.toString = function (t) {
                            var e = 'posthog';
                            return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
                        },
                        u.people.toString = function () {
                            return u.toString(1) + '.people (stub)';
                        },
                        o =
                            'init me ls _ps capture Ee calculateEventProperties $s register register_once register_for_session unregister unregister_for_session Ls getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags getFeatureFlagPayloads setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Ds Fs createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ps debug L qs getPageViewId captureTraceFeedback captureTraceMetric'.split(
                            ' '
                        ),
                        n = 0;
                    n < o.length;
                    n++
                )
                    g(u, o[n]);
                e._i.push([i, s, a]);
            }),
            (e.__SV = 1));
    })(document, window.posthog || []);
}

/* ────────────────────────────────────────────────────────────
   Click delegation for [data-ph-event].

   Any element with `data-ph-event="name"` fires that named event
   when clicked. Props come from:
     - `data-ph-props` (JSON string, optional)
     - every `data-ph-prop-<name>` attribute (converted to prop <name>)
   ────────────────────────────────────────────────────────────── */
function wireDelegatedClickTracking() {
    document.addEventListener(
        'click',
        (e) => {
            const el = e.target.closest('[data-ph-event]');
            if (!el) return;
            const name = el.getAttribute('data-ph-event');
            if (!name) return;

            const props = collectProps(el);
            capture(name, props);
        },
        true
    );
}

function collectProps(el) {
    const props = {};

    // JSON blob
    const blob = el.getAttribute('data-ph-props');
    if (blob) {
        try { Object.assign(props, JSON.parse(blob)); } catch { /* ignore */ }
    }

    // data-ph-prop-* attributes
    for (const attr of el.attributes) {
        if (attr.name.startsWith('data-ph-prop-')) {
            const key = attr.name.replace('data-ph-prop-', '').replace(/-/g, '_');
            props[key] = attr.value;
        }
    }

    // Outbound href detection
    if (el.tagName === 'A' && el.href) {
        const href = el.getAttribute('href');
        if (href && !href.startsWith('#') && !href.startsWith('/')) {
            props.$external = true;
            try { props.target_host = new URL(el.href).host; } catch { /* ignore */ }
        }
    }

    return props;
}

/* Re-capture pageview when Livewire morphs the DOM in-place
   (e.g. "Load more stories" button on /blog). Without this, users
   who scroll through paginated lists show only one pageview even
   though they loaded multiple content sets. */
function wireLivewireHooks() {
    document.addEventListener('livewire:initialized', () => {
        if (window.Livewire && typeof window.Livewire.hook === 'function') {
            window.Livewire.hook('morph.updated', () => {
                if (window.posthog && window.posthog.capture) {
                    capture('livewire_morph_completed', {});
                }
            });
        }
    });
}

/* Public helper — available as window.track() for any inline JS
   that needs to emit a custom event (e.g. the reservation form). */
export function capture(event, properties = {}) {
    if (window.posthog && window.posthog.capture) {
        window.posthog.capture(event, properties);
    }
}

window.track = capture;
